import { withConsentPilot } from "../../utils/consent";
import "./UpsellPageV2.css";
import { trackUpsellV2State } from "../../utils/upsellV2Analytics";
import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UPSELL_V2_PRODUCTS, UPSELL_V2_SEQUENCES, UPSELL_V2_PAYMENT_BENEFIT } from "../../constants/upsellsV2";
import { getUpsellV2Offer } from "../../utils/upsellV2Flow";
import { useUpsellV2Payment } from "../../hooks/useUpsellV2Payment";

const focusStyle =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#7C3A8D]";

export default function UpsellPageV2(props) {
  const location = useLocation();
  const preview = process.env.REACT_APP_PAYMENT_DEPLOY_ENV === "staging" &&
    new URLSearchParams(location.search).get("preview") === "1";
  return preview ? <UpsellPreviewV2 /> : <LiveUpsellPageV2 {...props} />;
}

function UpsellPreviewV2() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const key = Object.keys(UPSELL_V2_PRODUCTS).find(key => UPSELL_V2_PRODUCTS[key].route === location.pathname);
  const funnel = Object.keys(UPSELL_V2_SEQUENCES).find(funnel => UPSELL_V2_SEQUENCES[funnel].includes(key));
  const steps = UPSELL_V2_SEQUENCES[funnel || "coloranalysis"];
  const index = steps.indexOf(key);
  const advance = () => {
    const next = steps[(index + 1) % steps.length];
    navigate(`${UPSELL_V2_PRODUCTS[next].route}?preview=1&currency=${params.get("currency") === "eur" ? "eur" : "usd"}`);
  };
  return <UpsellViewV2 offer={getUpsellV2Offer(key, params.get("currency") === "eur" ? "eur" : "usd")}
    session={{ currentStep: index, steps }} add={advance} skip={advance} loading={false} disabled={false} skipDisabled={false} />;
}

function LiveUpsellPageV2({ paymentOptions } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { offer, session, add, skip, loading, disabled, skipDisabled } = useUpsellV2Payment({
    onState: trackUpsellV2State,
    ...paymentOptions,
    pathname: location.pathname,
    navigate: (target, options) => {
      if (target.startsWith("/") && !target.startsWith("//"))
        navigate(withConsentPilot(target), options);
      else window.location.replace(withConsentPilot(target));
    },
  });

  return <UpsellViewV2 {...{ offer, session, add, skip, loading, disabled, skipDisabled }} />;
}

function UpsellViewV2({ offer, session, add, skip, loading, disabled, skipDisabled }) {
  const heading = useRef(null);
  const offerTitle = offer?.headline;
  useLayoutEffect(() => {
    if (!offerTitle) return;
    heading.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    const previousTitle = document.title;
    document.title = `${offerTitle} | ColorAnalysisPro`;
    return () => {
      document.title = previousTitle;
    };
  }, [offerTitle]);

  if (!offer) {
    return (
      <main className="min-h-screen bg-[#F2F3F5] p-6 text-center text-gray-600">
        <p role="status">Continuing to your next step…</p>
      </main>
    );
  }

  const symbol = offer.currency === "eur" ? "€" : "$";
  const price = (amount) => `${symbol}${(amount / 100).toFixed(2)}`;
  const benefits = [UPSELL_V2_PAYMENT_BENEFIT, offer.guidance, offer.tryOn];
  return (
    <main className="upsell-v2">
      <article
        aria-labelledby="upsell-title"
        className="upsell-v2__card"
      >
        <header className="upsell-v2__header">
          <div>
            <p className="font-semibold">ColorAnalysisPro</p>
            <p className="upsell-v2__label">Special Offers</p>
          </div>
          <span className="upsell-v2__counter" aria-label={`Offer ${session.currentStep + 1} of ${session.steps.length}`}>
            {session.currentStep + 1}/{session.steps.length}
          </span>
        </header>

        <div className="upsell-v2__heading">
          <h1
            ref={heading}
            id="upsell-title"
            tabIndex={-1}
            className="upsell-v2__title"
          >
            {offer.headline}
          </h1>
        </div>

        <figure className="upsell-v2__preview">
          <img
            src={offer.image}
            alt={offer.imageAlt}
            width={offer.imageWidth}
            height={offer.imageHeight}
            decoding="async"
            className="upsell-v2__image"
          />
          <figcaption className="upsell-v2__caption">
            Example preview
          </figcaption>
        </figure>

        <section className="upsell-v2__pricing">
          <p className="upsell-v2__label">One-Time Offer for New Customers</p>
          <div
            aria-label="Offer price"
            className="flex items-center justify-center gap-3"
          >
            <del className="upsell-v2__original-price">
              <span className="sr-only">Original price </span>
              {price(offer.oldAmount)}
            </del>
            <span className="text-3xl font-bold text-[#7C3A8D]">
              <span className="sr-only">Offer price </span>
              {price(offer.amount)}
            </span>
            <span className="rounded-full border border-[#F4A896] bg-[#FFE6D1] px-2.5 py-1 text-xs font-semibold text-[#7C3A8D]">
              {offer.discountPercent}% OFF
            </span>
          </div>

        </section>

        <ul className="upsell-v2__benefits">
          {benefits.map((benefit) => (
            <li key={benefit} className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FFE6D1] text-xs font-bold text-[#7C3A8D]"
              >
                ✓
              </span>
              <span className="upsell-v2__benefit-text">
                {benefit}
              </span>
            </li>
          ))}
        </ul>

        <div className="upsell-v2__actions">
          <button
            type="button"
            onClick={add}
            disabled={disabled}
            className={`min-h-11 w-full rounded-lg bg-[#7C3A8D] px-3 py-2 text-sm font-semibold leading-snug text-white transition hover:bg-[#A259B5] disabled:cursor-wait disabled:opacity-70 ${focusStyle}`}
          >
            {loading ? "Processing…" : offer.cta || `Add ${offer.title} Offer`}
          </button>
          <p role="status" className="sr-only">
            {loading ? "Processing your payment." : ""}
          </p>
          <button
            type="button"
            onClick={skip}
            disabled={skipDisabled}
            className={`min-h-11 w-full rounded text-xs font-semibold text-[#111827] transition hover:text-[#7C3A8D] disabled:cursor-wait disabled:opacity-50 sm:text-sm ${focusStyle}`}
          >
            Skip the offer
          </button>
        </div>

        <footer className="upsell-v2__footer">
          <a
            href="https://color-analysis.pro/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={`rounded font-medium hover:text-[#7C3A8D] ${focusStyle}`}
          >
            Privacy
          </a>
          <span aria-hidden="true" className="text-gray-300">
            |
          </span>
          <a
            href="https://color-analysis.pro/terms#subscription"
            target="_blank"
            rel="noopener noreferrer"
            className={`rounded font-medium hover:text-[#7C3A8D] ${focusStyle}`}
          >
            Terms
          </a>
        </footer>
      </article>
    </main>
  );
}
